import { useEffect, useRef, useState } from 'react';
import { Flex, Typography } from 'antd';
import { unByKey } from 'ol/Observable';

import { useProject } from '@contexts/Project.context';
import { useCollectionStore } from '@stores/useCollectionStore';
import { createTooltip } from '@utils/map/tooltip.overlay';
import { formatLength } from '@utils/measurementSystem';
import { formatNumber } from '@utils/helpers';
import { objectLabel } from '../utils/transformUtils';

const { Text } = Typography;

const TOOLTIP_ID = 'collection-hexagon-tooltip';

/**
 * Builds the compact hover-tooltip body for a hexagon cell: total objects, mean
 * height, and a per-class count breakdown. Kept intentionally light — the full
 * stem/area breakdown lives in the click-through info panel, not the tooltip.
 */
const HexagonTooltipBody = ({ cell }) => {
	const objects = useCollectionStore(s => s.objects);
	const { count, mean_height } = cell.stats;
	const [heightNum, heightUnit] = mean_height != null ? formatLength(mean_height) : [];

	return (
		<Flex vertical gap={2}>
			<Text strong style={{ fontSize: '0.8rem', color: '#fff' }}>
				{formatNumber(count)} objects
			</Text>
			{heightNum && (
				<Text style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.85)' }}>
					Mean height: {heightNum} {heightUnit}
				</Text>
			)}
			{Object.entries(cell.detections).map(([className, stats]) => (
				<Text
					key={className}
					style={{ fontSize: '0.75rem', color: 'rgba(255, 255, 255, 0.85)' }}>
					{objectLabel(objects, className)}: {formatNumber(stats.count)}
				</Text>
			))}
		</Flex>
	);
};

/**
 * Hover-tooltip + click-to-select interactions for collection hexagon cells,
 * cloning the `useCollectionTooltip` pattern (portal overlay element + OL
 * `pointermove`/`click`) for the hexagon layer.
 *
 * Two design points:
 * - **Layer-scoped hit detection.** Feature lookup is filtered to the hexagon
 *   layer, and cells are matched by their `cellId` property (they carry no
 *   `node_uuid`), so this never picks up detection features.
 * - **Yields to detections.** Hexagons sit *below* the detection shapes, and the
 *   detection tooltip (`useCollectionTooltip`) already handles hover/click on
 *   `node_uuid` features. When a detection is under the cursor we suppress the
 *   hexagon tooltip and skip selection, so the two never compete for the same
 *   pixel — the detection (the more specific target) wins.
 *
 * Refs hold values read inside the long-lived OL handlers (the current `cells`
 * array) so the listeners don't need re-registering on every data change.
 *
 * @param {boolean} enabled - gate (typically the hexagon layer's visibility)
 * @param {import('ol/layer/WebGLVector').default|null} layer - the hexagon layer
 * @param {import('../utils/collectionHexagonsToCells').HexagonCell[]} cells
 * @param {React.RefObject<HTMLElement>} overlayElRef - imperatively-created overlay element
 * @returns {{ tooltipContent: React.ReactNode }}
 */
const useCollectionHexagonTooltip = (enabled, layer, cells, overlayElRef) => {
	const { mapObject, toolBarVisible, interactionsDisabled } = useProject();
	const setSelectedHexagonCellId = useCollectionStore(s => s.setSelectedHexagonCellId);

	// Read inside OL handlers without re-registering listeners when cells change.
	const cellsRef = useRef(cells);
	useEffect(() => {
		cellsRef.current = cells;
	});

	// Read inside the long-lived OL handlers without re-registering listeners
	// whenever these change identity — mirrors useCollectionTooltip's guard so
	// hexagon hover/select yields to a hidden toolbar or disabled interactions
	// the same way the detection tooltip already does.
	const toolBarVisibleRef = useRef(toolBarVisible);
	const interactionsDisabledRef = useRef(interactionsDisabled);
	useEffect(() => {
		toolBarVisibleRef.current = toolBarVisible;
		interactionsDisabledRef.current = interactionsDisabled;
	});

	const [tooltipContent, setTooltipContent] = useState(null);

	useEffect(() => {
		if (!mapObject || !enabled || !layer || !overlayElRef.current) return;

		const overlay = createTooltip({
			mapRef: mapObject,
			tooltipRef: overlayElRef.current,
			offset: [12, -12],
			positioning: 'bottom-left',
			id: TOOLTIP_ID,
		});

		// A detection feature (has node_uuid) under the cursor means the
		// detection tooltip owns this pixel — hexagons defer.
		const detectionAt = pixel =>
			mapObject.getFeaturesAtPixel(pixel)?.some(f => f.get('node_uuid'));

		// Resolve the hexagon cell under the cursor, scoped to the hexagon layer.
		const cellAt = pixel => {
			const feats = mapObject.getFeaturesAtPixel(pixel, { layerFilter: l => l === layer });
			const cellId = feats?.find(f => f.get('cellId') != null)?.get('cellId');
			if (cellId == null) return null;
			return cellsRef.current.find(c => c.id === cellId) ?? null;
		};

		const moveKey = mapObject.on('pointermove', e => {
			if (
				e.dragging ||
				!toolBarVisibleRef.current ||
				interactionsDisabledRef.current ||
				detectionAt(e.pixel)
			) {
				overlay.setPosition(undefined);
				return;
			}
			const cell = cellAt(e.pixel);
			if (!cell) {
				overlay.setPosition(undefined);
				return;
			}
			setTooltipContent(<HexagonTooltipBody cell={cell} />);
			overlay.setPosition(e.coordinate);
		});

		const clickKey = mapObject.on('click', e => {
			if (e.dragging || interactionsDisabledRef.current || detectionAt(e.pixel)) return;
			const cell = cellAt(e.pixel);
			if (!cell) return;
			// Toggle: clicking the already-selected cell deselects it. Read the
			// current selection via getState so the listener needn't re-register
			// when the selection changes.
			const currentId = useCollectionStore.getState().selectedHexagonCellId;
			setSelectedHexagonCellId(currentId === cell.id ? null : cell.id);
		});

		return () => {
			unByKey(moveKey);
			unByKey(clickKey);
			mapObject.removeOverlay(overlay);
		};
	}, [mapObject, enabled, layer, overlayElRef, setSelectedHexagonCellId]);

	return { tooltipContent };
};

export default useCollectionHexagonTooltip;
